import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, Copy, ArrowsClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { api, apiError } from "../lib/api";
import { copyText } from "../lib/clipboard";

const ANREDEN = [
  ["sie", "Sie (förmlich)"],
  ["du", "Du (persönlich)"],
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function MailDraftDialog({ typ, id, email, empfaengerName }) {
  const [open, setOpen] = useState(false);
  const [anrede, setAnrede] = useState("sie");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const reset = () => {
    setResult(null);
    setDetails("");
    setAnrede("sie");
    setSavedAt(null);
  };

  const loadSaved = async () => {
    try {
      const params = { typ };
      if (id) params.id = id;
      if (email) params.email = email;
      const { data } = await api.get("/berater/mail-entwurf", { params });
      if (data?.exists) {
        setResult({ betreff: data.betreff, text: data.text, kontakt: data.kontakt });
        setAnrede(data.anrede || "sie");
        setDetails(data.details || "");
        setSavedAt(data.am || null);
      }
    } catch {
      /* ignore */
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const payload = { typ, anrede, details };
      if (id) payload.id = id;
      if (email) payload.email = email;
      const { data } = await api.post("/berater/mail-entwurf", payload);
      setResult(data);
      setSavedAt(data.am || null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!result) return;
    const full = (result.betreff ? `Betreff: ${result.betreff}\n\n` : "") + result.text;
    const ok = await copyText(full);
    toast[ok ? "success" : "error"](ok ? "Entwurf kopiert – jetzt in Outlook o.ä. einfügen." : "Kopieren nicht möglich.");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) loadSaved(); else reset(); }}>
      <DialogTrigger asChild>
        <button
          data-testid="mail-draft-trigger"
          className="mt-2 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-[#0A1128]/15 bg-white text-xs font-semibold text-[#0A1128] transition-colors hover:bg-[#F1F5F9]"
        >
          <Sparkle size={14} weight="fill" color="#C5A059" /> Mail-Entwurf (KI)
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-2xl" data-testid="mail-draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">KI-Mail-Entwurf</DialogTitle>
          <DialogDescription>
            {empfaengerName ? `Für ${empfaengerName}. ` : ""}Zum Kopieren – aus deinem Mail-Programm (z.&nbsp;B. Outlook) versenden.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Anrede</p>
            <div className="grid grid-cols-2 gap-2">
              {ANREDEN.map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`mail-anrede-${v}`}
                  onClick={() => setAnrede(v)}
                  className={`h-10 rounded-lg border text-sm font-semibold transition-colors ${anrede === v ? "border-[#C5A059] bg-[#C5A059]/10 text-[#8a6d2f]" : "border-[#E2E8F0] bg-white text-[#4A5568]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Eigene Details (optional)</p>
            <Textarea
              data-testid="mail-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="z. B. Bezug auf gemeinsamen Bekannten, konkreter Terminvorschlag, lockerer Ton …"
              className="min-h-[64px] rounded-lg border-[#E2E8F0] text-sm"
            />
          </div>
          {!result ? (
            <button
              data-testid="mail-generate-btn"
              onClick={generate}
              disabled={busy}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#0A1128] text-sm font-semibold text-white transition-opacity active:scale-[0.99] disabled:opacity-60"
            >
              {busy ? "Entwurf wird erstellt…" : (<><Sparkle size={16} weight="fill" color="#C5A059" /> Entwurf erstellen</>)}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="max-h-[38vh] overflow-y-auto rounded-xl border border-[#E2E8F0] bg-[#F8F9FA] p-3" data-testid="mail-draft-result">
                {savedAt && (
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[#A0AEC0]" data-testid="mail-draft-savedat">
                    <ClockCounterClockwise size={12} /> Zuletzt erstellt: {fmtDate(savedAt)}
                  </p>
                )}
                {result.betreff && <p className="mb-1.5 text-sm font-semibold text-[#0A1128]">Betreff: {result.betreff}</p>}
                <p className="whitespace-pre-line text-sm leading-relaxed text-[#4A5568]">{result.text}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="mail-copy-btn" onClick={copyAll} className="flex h-11 items-center justify-center gap-2 rounded-xl bg-[#C5A059] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">
                  <Copy size={16} /> Kopieren
                </button>
                <button data-testid="mail-regenerate-btn" onClick={generate} disabled={busy} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] active:scale-[0.99] disabled:opacity-60">
                  <ArrowsClockwise size={16} /> {busy ? "…" : "Neuer Vorschlag"}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
